import { colorForId } from "@memo-air/react";
import { useUser } from "@app/lib/swr/user";
import type { LiveDocSummaryType } from "@app/types/live_docs";
import { formatDistanceToNow } from "date-fns";

const maxAvatars = 3;

interface LiveDocActivityProps {
  summary: LiveDocSummaryType | undefined;
  fallbackEditedAt: number;
}

export function LiveDocActivity({
  summary,
  fallbackEditedAt,
}: LiveDocActivityProps) {
  const { user } = useUser();
  const present = summary?.present ?? [];
  const writer = summary?.writing[0];
  const editor = summary?.lastEditor;
  const editedAt = summary?.lastEditedAt
    ? Date.parse(summary.lastEditedAt)
    : fallbackEditedAt;
  const pending = summary?.pending ?? [];
  const needsYou = pending.filter(
    (suggestion) => user && suggestion.approverId === user.sId
  ).length;

  return (
    <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground dark:text-muted-foreground-night">
      {present.length > 0 && (
        <span
          className="flex shrink-0 -space-x-1"
          aria-label={`In the doc now: ${present.map((actor) => actor.name).join(", ")}`}
        >
          {present.slice(0, maxAvatars).map((actor) => (
            <span
              key={actor.id}
              title={actor.name}
              className="flex h-4 w-4 items-center justify-center rounded-full border border-background text-[9px] font-semibold text-white dark:border-background-night"
              style={{ backgroundColor: colorForId(actor.id) }}
            >
              {actor.kind === "agent"
                ? "✦"
                : actor.name.charAt(0).toUpperCase()}
            </span>
          ))}
        </span>
      )}
      {writer ? (
        <span className="truncate font-medium text-foreground dark:text-foreground-night">
          ✦ {writer.name} writing{writer.section ? ` · ${writer.section}` : "…"}
        </span>
      ) : (
        <span className="truncate">
          edited
          {editor
            ? ` by ${editor.kind === "agent" ? "✦ " : ""}${editor.name}`
            : ""}{" "}
          {formatDistanceToNow(editedAt, { addSuffix: true })}
        </span>
      )}
      {needsYou > 0 ? (
        <span className="shrink-0 font-semibold text-highlight-600 dark:text-highlight-400">
          · {needsYou} {needsYou === 1 ? "needs" : "need"} you
        </span>
      ) : pending.length > 0 ? (
        <span className="shrink-0">· {pending.length} to review</span>
      ) : null}
    </span>
  );
}
